import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { mobile, tablet } from 'services/ui';
import { Colors } from 'context';
import { PATHS } from 'router/config';

interface IGrowthFlow {
  /** Сколько навыков подтверждено школой и заявлено самим. */
  skillsMine: number;
  skillsMissing: number;
  /** Профессия, к которой студент ближе всего, и её посадка. */
  bestProfession: { code: string; title: string; haveCount: number; totalCount: number } | null;
  professionsTotal: number;
  /** Вакансий В НАШЕЙ ЛЕНТЕ по лучшей профессии — не «на рынке». */
  liveVacancies: number;
}

/**
 * Схема связей раздела (ТЗ 0039, доводка UX).
 *
 * Прямая претензия владельца: «нету именно связи между ними, чтобы было
 * понятно, что с чем связано и зачем». Разделы существовали как четыре
 * независимых экрана, и цепочка курс → навык → профессия → вакансия нигде не
 * была нарисована — её надо было держать в голове.
 *
 * 🔴 Своей библиотеки диаграмм в ЛМС нет и заводить её ради четырёх
 * прямоугольников не стоит: рисуем разметкой, стрелки — псевдоэлементами.
 * На узком экране цепочка переворачивается в столбик, и стрелки вниз —
 * поэтому направление задаётся не картинкой, а отдельным элементом.
 *
 * Числа настоящие. Схема с выдуманными цифрами хуже отсутствующей: один раз
 * увидев несовпадение со страницей навыков, ей перестанут верить целиком.
 */
export const GrowthFlow = memo(
  ({ skillsMine, skillsMissing, bestProfession, professionsTotal, liveVacancies }: IGrowthFlow) => {
    const { t } = useTranslation();

    return (
      <Card>
        <Head>
          <SectionTitle>{t('growth.flow.title')}</SectionTitle>
          <Lead>{t('growth.flow.lead')}</Lead>
        </Head>

        <Chain>
          {/* На «Мои курсы», а не на сам хаб: узел стоял ссылкой на страницу,
              где уже находишься, и «Otwórz →» не делал ничего. */}
          <Node to={PATHS.courses}>
            <NodeStep>1</NodeStep>
            <NodeTitle>{t('growth.flow.courses')}</NodeTitle>
            <NodeText>{t('growth.flow.coursesText')}</NodeText>
            <NodeGo>{t('growth.flow.go')}</NodeGo>
          </Node>

          <Arrow aria-hidden="true">→</Arrow>

          <Node to={PATHS.growthSkills}>
            <NodeStep>2</NodeStep>
            <NodeTitle>{t('growth.flow.skills')}</NodeTitle>
            <NodeText>
              {t('growth.flow.skillsText', { have: skillsMine, missing: skillsMissing })}
            </NodeText>
            <NodeGo>{t('growth.flow.go')}</NodeGo>
          </Node>

          <Arrow aria-hidden="true">→</Arrow>

          {/* На СПИСОК профессий, а не на ближайшую. Все четыре узла цепочки
              ведут в свои разделы, и один, ныряющий в конкретную запись,
              обманывает: подпись обещает раздел «Профессия», а открывалась
              страница «Свой стартап». Имя ближайшей остаётся в тексте. */}
          <Node to={PATHS.growthProfessions}>
            <NodeStep>3</NodeStep>
            <NodeTitle>{t('growth.flow.profession')}</NodeTitle>
            <NodeText>
              {bestProfession
                ? t('growth.flow.professionText', {
                    title: bestProfession.title,
                    have: bestProfession.haveCount,
                    total: bestProfession.totalCount,
                  })
                : t('growth.flow.professionEmpty', { n: professionsTotal })}
            </NodeText>
            <NodeGo>{t('growth.flow.go')}</NodeGo>
          </Node>

          <Arrow aria-hidden="true">→</Arrow>

          <Node to={PATHS.careerVacancies}>
            <NodeStep>4</NodeStep>
            <NodeTitle>{t('growth.flow.vacancies')}</NodeTitle>
            {/* «В нашей ленте» — не «на рынке». Мы видим только свою доску, и
                выдавать её за рынок нельзя. */}
            <NodeText>{t('growth.flow.vacanciesText', { n: liveVacancies })}</NodeText>
            <NodeGo>{t('growth.flow.go')}</NodeGo>
          </Node>
        </Chain>

        <Aside>
          <AsideIcon aria-hidden="true">★</AsideIcon>
          <AsideBody>
            <AsideTitle>{t('growth.flow.medalsTitle')}</AsideTitle>
            <AsideText>{t('growth.flow.medalsText')}</AsideText>
          </AsideBody>
          <AsideLink to={PATHS.growthAchievements}>{t('growth.flow.medalsLink')}</AsideLink>
        </Aside>
      </Card>
    );
  },
);

const Card = styled.section`
  padding: 24px;
  border-radius: 12px;
  border: 1px solid ${Colors.gray};
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Head = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  ${fontService.getFont({ size: 20, lineHeight: 26, weight: FontWeight.bold })}
`;

const Lead = styled.p`
  margin: 0;
  max-width: 720px;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const Chain = styled.div`
  display: flex;
  align-items: stretch;
  gap: 8px;

  ${tablet} {
    flex-wrap: wrap;
  }

  ${mobile} {
    flex-direction: column;
  }
`;

const Node = styled(Link)`
  flex: 1 1 0;
  min-width: 0;
  padding: 16px;
  border-radius: 10px;
  background: ${Colors.bg_gray};
  color: ${Colors.black};
  display: flex;
  flex-direction: column;
  gap: 6px;
  transition: background 0.2s ease;

  &:hover {
    background: ${Colors.gray};
    color: ${Colors.black};
  }
`;

const NodeStep = styled.span`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Жёлтый — ЗАЛИВКА, текст поверх неё ТОЛЬКО btn_text: black в тёмной
     теме равен #ECECEC и на жёлтом исчезает (см. правило в colors.ts). */
  background: ${Colors.main};
  color: ${Colors.btn_text};
  ${fontService.text({ type: TextType.small, weight: FontWeight.bold })}
`;

const NodeTitle = styled.span`
  ${fontService.text({ type: TextType.medium, weight: FontWeight.bold })}
`;

const NodeText = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

/* Подпись-переход. Без неё узлы читались как справочные плашки: они ведут на
   свои разделы, но ничего в их виде об этом не говорило, и вся навигация
   раздела оказывалась спрятанной. `margin-top: auto` держит подпись у нижнего
   края, чтобы у карточек разной высоты она стояла в одну линию. */
const NodeGo = styled.span`
  margin-top: auto;
  padding-top: 6px;
  text-decoration: underline;
  text-underline-offset: 3px;
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const Arrow = styled.span`
  flex: 0 0 auto;
  align-self: center;
  color: ${Colors.gray_dark};
  ${fontService.getFont({ size: 20, lineHeight: 26, weight: FontWeight.medium })}

  ${mobile} {
    /* В столбик стрелка обязана смотреть вниз, иначе цепочка читается как
       «вправо» там, где визуально идёт вниз. */
    transform: rotate(90deg);
  }
`;

const Aside = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 10px;
  background: ${Colors.bg_gray};
  flex-wrap: wrap;
`;

const AsideIcon = styled.span`
  width: 32px;
  height: 32px;
  flex: 0 0 auto;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${Colors.main};
  color: ${Colors.btn_text};
  ${fontService.text({ type: TextType.small, weight: FontWeight.bold })}
`;

const AsideBody = styled.div`
  flex: 1 1 220px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const AsideTitle = styled.span`
  ${fontService.text({ type: TextType.small, weight: FontWeight.bold })}
`;

const AsideText = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const AsideLink = styled(Link)`
  flex: 0 0 auto;
  color: ${Colors.black};
  text-decoration: underline;
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}

  &:hover {
    color: ${Colors.black};
  }
`;
